import { useEffect, useState } from 'react'
import { CheckCircle, Zap, Lock } from 'lucide-react'
import { advancedApi, type UsageResponse } from '../api'
import { useToastStore } from '../store/toast'

interface UsageMeterProps {
  compact?: boolean
}

export default function UsageMeter({ compact = false }: UsageMeterProps) {
  const addToast = useToastStore((state) => state.addToast)
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    advancedApi
      .getUsage()
      .then((res) => setUsage(res.data))
      .catch((err) => {
        console.error('Failed to load usage:', err)
        addToast({ type: 'error', message: 'Could not load usage stats.' })
      })
      .finally(() => setLoading(false))
  }, [addToast])

  if (loading) {
    return (
      <div className="animate-pulse h-20 bg-gray-100 rounded-lg" />
    )
  }

  if (!usage) {
    return null
  }

  const isFree = usage.plan_type === 'free'
  const isPro = usage.plan_type === 'pro'
  const isAdvanced = usage.plan_type === 'advanced'

  // Calculate progress percentages
  const pdfProgress = usage.pdfs.limit > 0 
    ? Math.min(100, (usage.pdfs.used / usage.pdfs.limit) * 100) 
    : 0
  
  const chandraProgress = usage.chandra.limit > 0 
    ? Math.min(100, (usage.chandra.used / usage.chandra.limit) * 100) 
    : 0

  if (compact) {
    return (
      <div className="flex items-center gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          {usage.pdfs.remaining === -1 ? '∞ PDFs' : `${usage.pdfs.remaining} PDFs left`}
        </span>
        <span className="flex items-center gap-1">
          <Zap className="w-3 h-3" />
          {isFree ? '1-page test mode' : 'Advanced scan enabled'}
        </span>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Plan badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isAdvanced ? (
            <span className="text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-3 py-1">
              Advanced Plan
            </span>
          ) : isPro ? (
            <span className="text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-3 py-1">
              Pro Plan
            </span>
          ) : (
            <span className="text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 rounded-full px-3 py-1">
              Free Plan
            </span>
          )}
        </div>
        <a
          href="/settings#subscription"
          className="text-xs text-brand-blue hover:underline"
        >
          {isFree ? 'Upgrade →' : 'Manage plan'}
        </a>
      </div>

      {/* PDF uploads */}
      {usage.pdfs.limit > 0 && (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-600">PDF uploads</span>
            <span className="font-medium text-gray-900">
              {usage.pdfs.used}/{usage.pdfs.limit}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                pdfProgress > 80 ? 'bg-red-500' : pdfProgress > 50 ? 'bg-amber-500' : 'bg-green-500'
              }`}
              style={{ width: `${pdfProgress}%` }}
            />
          </div>
          {usage.pdfs.remaining === 0 && (
            <p className="text-xs text-red-600 mt-1">
              Limit reached. Upgrade to Pro for unlimited uploads.
            </p>
          )}
        </div>
      )}

      {/* Advanced scans */}
      {!isFree && usage.chandra.limit > 0 && (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-600">Advanced OCR scans</span>
            <span className="font-medium text-gray-900">
              {usage.chandra.used}/{usage.chandra.limit === -1 ? '∞' : usage.chandra.limit}
            </span>
          </div>
          {usage.chandra.limit > 0 && (
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  chandraProgress > 80 ? 'bg-red-500' : chandraProgress > 50 ? 'bg-amber-500' : 'bg-brand-blue'
                }`}
                style={{ width: `${chandraProgress}%` }}
              />
            </div>
          )}
          {usage.chandra.remaining === 0 && (
            <p className="text-xs text-red-600 mt-1">
              Quota exhausted. Upgrade to Advanced for unlimited scans.
            </p>
          )}
        </div>
      )}

      {/* Feature availability */}
      <div className="pt-3 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-700 mb-2">Your features:</p>
        <div className="space-y-1">
          <FeatureRow available={true} label="Standard extraction" />
          <FeatureRow available={true} label="Column selection" />
          <FeatureRow available={isPro || isAdvanced} label="Advanced OCR" />
          <FeatureRow available={isPro || isAdvanced} label="Batch upload" />
          <FeatureRow available={isPro || isAdvanced} label="Priority processing" />
          <FeatureRow available={isAdvanced} label="Split-screen viewer" />
          <FeatureRow available={isAdvanced} label="Team collaboration" />
        </div>
      </div>
    </div>
  )
}

function FeatureRow({ available, label }: { available: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {available ? (
        <CheckCircle className="w-3 h-3 text-green-500" />
      ) : (
        <Lock className="w-3 h-3 text-gray-300" />
      )}
      <span className={available ? 'text-gray-700' : 'text-gray-400'}>
        {label}
      </span>
    </div>
  )
}
